import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  TextField,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  AccordionDetails,
  AccordionSummary,
  Accordion,
  Tooltip
} from "@material-ui/core";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
import moment from "moment";
import VisibilityIcon from "@material-ui/icons/Visibility";
import auditEndpoint from "app/api/services/auditEndpoint";
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core';
import styles from './styles';
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
const useStyles = makeStyles(styles);
const OPERACOES_DISPONIVEIS = ["INSERT", "UPDATE", "DELETE", "SEARCH"];
const PERFIS_DISPONIVEIS = ["INTERNO", "EXTERNO", "ANONIMO"];

export default function Audit() {

  const classes = useStyles();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [loading, setLoading] = useState(false);
  const initialLoad = React.useRef(true);
  const [auditSearchEnabled, setAuditSearchEnabled] = useState(false);
  const initialFilters = useMemo(() => ({ dtInicial: null, dtFinal: null, cdUsuario: '', nmUsuario: '', deEntidade: '', cdEntidade: '', deEndpoint: '', operacoes: [], perfis: [] }), []);
  const [filters, setFilters] = useState(initialFilters);
  const filtersRef = React.useRef(filters);

  const operacoesFiltradas = useMemo(() => {
    return auditSearchEnabled ? OPERACOES_DISPONIVEIS : OPERACOES_DISPONIVEIS.filter(op => op !== "SEARCH");
  }, [auditSearchEnabled]);

  useEffect(() => {
    const fetchAuditSearchEnabled = async () => {
      try {
        setAuditSearchEnabled(await auditEndpoint.getAuditSearchEnabled());
      } catch (e) {
        setAuditSearchEnabled(false);
      }
    };
    fetchAuditSearchEnabled();
  }, []);

  const handleSearch = useCallback(
    async (resetPage = false, filtros) => {
      if (!filtros?.dtInicial || !filtros?.dtFinal) {
        alert("Por favor, preencha as datas inicial e final.");
        return;
      }
      setLoading(true);
      const currentPage = resetPage ? 0 : page;
      const search = { page: currentPage, size: pageSize, request: filtros };
      try {
        const response = await auditEndpoint.getAudits(search);
        if (response.status === 200 && response.data) {
          const content = response.data.content.map((row) => ({
            ...row,
            id: row.cdAuditoria,
          }));
          setData(content);
          setTotal(response.data.totalElements);
          if (resetPage) {
            setPage(0);
          }
        }
      } finally {
        setLoading(false);
      }
    },
    [page, pageSize]
  );

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    if (initialLoad.current) {
      initialLoad.current = false;
      return;
    }
    handleSearch(false, filtersRef.current);
  }, [page, pageSize, handleSearch]);

  const handleClear = () => {
    setFilters(initialFilters);
    setData([]);
    setTotal(0);
    setPage(0);
  };

  const handleViewDetails = (row) => {
    let parsed;
    try {
      parsed = row.jsonAuditoria ? JSON.parse(row.jsonAuditoria) : {};
    } catch {
      parsed = {};
    }
    setSelectedAudit({ ...row, parsedJson: parsed });
    setDialogOpen(true);
  };

  const formatDate = (val) => {
    const date = moment(val);
    if (!date.isValid()) return String(val);
    const formattedDate = date.format("DD/MM/YYYY");
    const formattedTime = date.format("HH:mm:ss");
    return (
      <div>
        <span>{formattedDate}</span>
        <span style={{ fontSize: '0.75em', color: '#777', marginLeft: 8 }}>{formattedTime}
      </span>
      </div>
    );
  };

  const formatIfDate = (val) => {
    if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}T/.test(val)) {
      return formatDate(val);
    }
    if (typeof val === "number" && val.toString().length === 13) {
      return formatDate(val);
    }
    return String(val);
  };

  const flattenChanges = (obj, path = []) => {
    let rows = [];
    Object.entries(obj).forEach(([key, value]) => {
      const currentPath = [...path, key];
      if (value && typeof value === "object" && ("anterior" in value || "atual" in value)) {
        rows.push({ path: currentPath, anterior: value.anterior, atual: value.atual });
      } else if (value && typeof value === "object") {
        rows = rows.concat(flattenChanges(value, currentPath));
      }
    });
    return rows;
  };

  const handlePrint = () => {
    const printContents = document.getElementById('conteudo-modal-impressao')?.innerHTML;
    const win = window.open('', '', 'height=720,width=1280');
    win.document.write('<html><head><title>Impressão</title>');
    win.document.write('<style>body{font-family:sans-serif;padding:20px;} table{width:100%;border-collapse:collapse;} td,th{border:1px solid #ccc;padding:8px;}</style>');
    win.document.write('</head><body>');
    win.document.write(printContents);
    win.document.write('</body></html>');
    win.document.close();
    win.print();
  };

  const renderModalContent = () => {
    const operacao = selectedAudit?.deOperacao;
    const json = selectedAudit?.parsedJson || {};

    const formatValue = (value) => {
      if (value == null) return '';
      if (typeof value === "number" && value.toString().length === 13) {
        return formatIfDate(value);
      }
      if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
        return formatIfDate(value);
      }
      if (typeof value === "string" && value.trim().startsWith("{") && value.trim().endsWith("}")) {
        try {
          const parsed = JSON.parse(value);
          if (typeof parsed === "object" && parsed !== null) {
            return <pre style={{ margin: 0 }}>{JSON.stringify(parsed, null, 2)}</pre>;
          }
        } catch (e) {
        }
      }
      if (typeof value === "object") {
        return <pre style={{ margin: 0 }}>{JSON.stringify(value, null, 2)}</pre>;
      }
      return String(value);
    };

    const renderFieldPath = (path) => (
      <>
        {path.map((segment, index) => (
          <Typography variant="body2" key={index} style={{ paddingLeft: index * 16 }}>
            {index === 0 ? segment : `↳ ${segment}`}
          </Typography>
        ))}
      </>
    );

    const renderTableHeader = (headers) => (
      <TableHead style={{ backgroundColor: '#f5f5f5' }}>
        <TableRow>
          {headers.map((label, idx) => (
            <TableCell key={idx} className={classes.tableCellBorder}>
              <strong>{label}</strong>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );

    const renderUpdateRows = () =>
      flattenChanges(json).map(({ path, anterior, atual }) => (
        <TableRow key={path.join(".")}>
          <TableCell className={`${classes.tableCellBorder} ${classes.campoColumn}`}>{renderFieldPath(path)}</TableCell>
          <TableCell className={classes.tableCellOldValue}>{formatValue(anterior)}</TableCell>
          <TableCell className={classes.tableCellNewValue}>{formatValue(atual)}</TableCell>
        </TableRow>
      ));

    const renderInsertDeleteRows = () =>
      Object.entries(json).map(([key, value]) => (
        <TableRow key={key}>
          <TableCell className={`${classes.tableCellBorder} ${classes.campoColumn}`}>{key}</TableCell>
          <TableCell className={classes.tableCellBorder}>{formatValue(value)}</TableCell>
        </TableRow>
      ));

    const renderTable = (headers, rows) => (
      <TableContainer>
        <Table>
          {renderTableHeader(headers)}
          <TableBody>{rows}</TableBody>
        </Table>
      </TableContainer>
    );

    if (operacao === "UPDATE") {
      return renderTable(["Campo", "Valor antigo", "Valor novo"], renderUpdateRows());
    }

    if (operacao === "INSERT" || operacao === "DELETE") {
      return renderTable(["Campo", "Valor"], renderInsertDeleteRows());
    }

    return <Typography>Sem conteúdo para exibir.</Typography>;
  };

  return (
    <MuiPickersUtilsProvider utils={MomentUtils}>
      {loading && (
        <div className={classes.centerProgress}>
          <CircularProgress size={28} />
        </div>
      )}
      <Box p={2}>
        <Typography variant="h5" gutterBottom><strong>Relatório de auditoria</strong></Typography>
        <Paper style={{ padding: 24, marginTop: 24 }}>
          <Typography variant="subtitle2"><strong>Filtros</strong></Typography>
          <Grid container spacing={3} direction="column">
            <Grid container item spacing={3}>
              <Grid item xs={6} sm={3} md={1}>
                <KeyboardDatePicker
                  label="Data inicial"
                  format="DD/MM/YYYY"
                  value={filters.dtInicial || null}
                  onChange={(date) => setFilters({ ...filters, dtInicial: date })}
                  required={true}
                />
              </Grid>
              <Grid item xs={6} sm={3} md={1}>
                <KeyboardDatePicker
                  label="Data final"
                  format="DD/MM/YYYY"
                  value={filters.dtFinal || null}
                  onChange={(date) => setFilters({ ...filters, dtFinal: date })}
                  required={true}
                />
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle3"><strong>Mais filtros</strong></Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pb: 2 }}>
                  <Box display="flex" flexDirection="column" width="100%">
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={4} md={3}>
                        <TextField label="Endpoint" value={filters.deEndpoint} fullWidth onChange={(e) => setFilters({ ...filters, deEndpoint: e.target.value || undefined })} />
                      </Grid>
                      <Grid item xs={12} sm={4} md={2}>
                        <TextField label="Usuário" value={filters.cdUsuario} fullWidth onChange={(e) => setFilters({ ...filters, cdUsuario: e.target.value || undefined })} />
                      </Grid>
                      <Grid item xs={12} sm={4} md={3}>
                        <TextField label="Nome" value={filters.nmUsuario} fullWidth onChange={(e) => setFilters({ ...filters, nmUsuario: e.target.value || undefined })} />
                      </Grid>
                    </Grid>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={2}>
                        <TextField label="Entidade" value={filters.deEntidade} fullWidth onChange={(e) => setFilters({ ...filters, deEntidade: e.target.value || undefined })} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <TextField label="Chave" value={filters.cdEntidade} fullWidth onChange={(e) => setFilters({ ...filters, cdEntidade: e.target.value || undefined })} />
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="subtitle1">Perfis:</Typography>
                        {PERFIS_DISPONIVEIS.map((op) => (
                          <FormControlLabel
                            key={op}
                            control={
                              <Checkbox
                                checked={filters.perfis?.includes(op) || false}
                                onChange={(e) => {
                                  const newOps = filters.perfis || [];
                                  const updated = e.target.checked ? [...newOps, op] : newOps.filter((o) => o !== op);
                                  setFilters({ ...filters, perfis: updated });
                                }}
                              />
                            }
                            label={{ INTERNO: "Interno", EXTERNO: "Externo", ANONIMO: "Anônimo" }[op]}
                          />
                        ))}
                      </Grid>
                    </Grid>
                  </Box>
                </AccordionDetails>
              </Accordion>

            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1">Tipo de operação:</Typography>
              {operacoesFiltradas.map((op) => (
                <FormControlLabel
                  key={op}
                  control={
                    <Checkbox
                      checked={filters.operacoes?.includes(op) || false}
                      onChange={(e) => {
                        const newOps = filters.operacoes || [];
                        const updated = e.target.checked
                          ? [...newOps, op]
                          : newOps.filter((o) => o !== op);
                        setFilters({ ...filters, operacoes: updated });
                      }}
                    />
                  }
                  label={{ INSERT: "Inclusão", UPDATE: "Alteração", DELETE: "Exclusão", SEARCH: "Consulta" }[op]}
                />
              ))}
            </Grid>
            <Grid item xs={12} style={{ textAlign: "right" }}>
              <Button onClick={handleClear} variant="outlined" style={{ marginRight: 8 }}>Limpar</Button>
              <Button onClick={() => handleSearch(true, filters)} variant="contained" color="primary">Consultar</Button>
            </Grid>
          </Grid>
        </Paper>

        <Paper style={{ padding: 16, marginTop: 32 }}>
          <Typography variant="subtitle2">Resultados</Typography>
          <TableContainer style={{ marginTop: 16 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headerCell} style={{ width: '180px' }}>Data / hora</TableCell>
                  <TableCell className={classes.headerCell} style={{ width: '150px' }}>Usuário</TableCell>
                  <TableCell className={classes.headerCell} style={{ width: '100px' }}>Perfil</TableCell>
                  <TableCell className={classes.headerCell} style={{ width: '300px' }}>Nome</TableCell>
                  <TableCell className={classes.headerCell} style={{ width: '200px' }}>Entidade</TableCell>
                  <TableCell className={classes.headerCell} style={{ width: '100px' }}>Chave</TableCell>
                  <TableCell className={classes.headerCell} style={{ width: '500px' }}>Endpoint</TableCell>
                  <TableCell className={classes.headerCell} style={{ width: '100px', textAlign: 'center' }}>Operação</TableCell>
                  <TableCell className={classes.headerCell}>Dados</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row) => (
                  <TableRow key={row.id} className={classes.zebraRow}>
                    <TableCell className={classes.allCells}>{formatIfDate(row.dtAuditoria)}</TableCell>
                    <TableCell className={classes.allCells}>{row.cdUsuario}</TableCell>
                    <TableCell className={classes.allCells}>
                      {row.dePerfil ? (
                        <span className={`${classes.operationLabel} ${classes[`perfil${row.dePerfil.charAt(0) + row.dePerfil.slice(1).toLowerCase()}`]}`}>
                          {row.dePerfil.charAt(0) + row.dePerfil.slice(1).toLowerCase()}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell className={classes.allCells}>{row.nmUsuario}</TableCell>
                    <TableCell className={classes.allCells}>{row.deEntidade}</TableCell>
                    <TableCell className={classes.allCells}>{row.cdEntidade?.split("=")[1]}</TableCell>
                    <TableCell className={classes.allCells}>{row.deEndpoint}</TableCell>
                    <TableCell className={classes.allCells}>
                      <Box className={classes.operationBox}>
                        {{
                          SEARCH: (<span className={`${classes.operationLabel} ${classes.iconSearch}`}>Consulta</span>),
                          UPDATE: (<span className={`${classes.operationLabel} ${classes.iconUpdate}`}>Alteração</span>),
                          INSERT: (<span className={`${classes.operationLabel} ${classes.iconInsert}`}>Inclusão</span>),
                          DELETE: (<span className={`${classes.operationLabel} ${classes.iconDelete}`}>Exclusão</span>)
                        }[row.deOperacao] || row.deOperacao}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {row.deOperacao !== 'SEARCH' && (
                        <Tooltip title="Detalhe da auditoria">
                          <IconButton onClick={() => handleViewDetails(row)}>
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>

                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, newPage) => {
              setPage(newPage);
            }}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 20, 50]}
            labelRowsPerPage="Linhas por página"
          />
        </Paper>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth={false} PaperProps={{ style: { width: 'auto', maxWidth: '95vw' } }}>
          <DialogTitle sx={{ fontWeight: 'bold' }}>Detalhe da auditoria</DialogTitle>
          <DialogContent dividers>
            <div id="conteudo-modal-impressao">
              <Grid container spacing={2}>
                <Grid item xs={6} sm={2}>
                  <Typography variant="body1">
                    <strong>Tipo:</strong> {selectedAudit?.deOperacao}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={5}>
                  <Typography variant="body1">
                    <strong>Entidade:</strong> {selectedAudit?.deEntidade}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={5}>
                  <Typography variant="body1">
                    <strong>Chave:</strong> {selectedAudit?.cdEntidade}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="body1">
                    <strong>Usuário:</strong> {selectedAudit?.cdUsuario}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={9}>
                  <Typography variant="body1">
                    <strong>Nome:</strong> {selectedAudit?.nmUsuario}
                  </Typography>
                </Grid>
              </Grid>
              <Box mt={2}>{renderModalContent()}</Box>
            </div>
          </DialogContent>
          <DialogActions style={{ padding: 24 }}>
            <Box display="flex" justifyContent="flex-end" width="100%">
              <Button onClick={handlePrint} color="primary" variant="outlined">Imprimir</Button>
              <Button onClick={() => setDialogOpen(false)} color="primary" variant="outlined" style={{ marginLeft: 8 }}>Fechar</Button>
            </Box>
          </DialogActions>

        </Dialog>
      </Box>
    </MuiPickersUtilsProvider>
  );
}
